/* eslint-disable indent */
import { useUser } from '../hooks/useUser';
import { chatSubscription, closeChat, fetchChats } from '../services/fetch-utils';
import { createContext, useState, useEffect, SetStateAction, Dispatch } from 'react';
import { ChatMessages, ChatsList } from '../interfaces/userInterfaces';
import { PostgrestSingleResponse } from '@supabase/supabase-js';

type UserMessagesType = {
  activeChatId: number;
  editingMessage: boolean;
  activeOpenChat: ChatsList;
  messageToEdit: ChatMessages;
  unreadMessagesCount: number;
  listOfOpenChats: ChatsList[];
  chatMessages: ChatMessages[];
  setActiveChatId: Dispatch<SetStateAction<number>>;
  setEditingMessage: Dispatch<SetStateAction<boolean>>;
  setActiveOpenChat: Dispatch<SetStateAction<ChatsList>>;
  setMessageToEdit: Dispatch<SetStateAction<ChatMessages>>;
  setUnreadMessagesCount: Dispatch<SetStateAction<number>>;
  setListOfOpenChats: Dispatch<SetStateAction<ChatsList[]>>;
  setChatMessages: Dispatch<SetStateAction<ChatMessages[]>>;
  chatUpdater: (payload: any) => any;
  pessimisticDeleteChatMessage: (message: ChatMessages, status: any) => any;
};

export const ChatMessageContext = createContext<UserMessagesType>({} as UserMessagesType);

export const ChatMessageProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();

  // ID of Chat that User is currently interacting with
  const [activeChatId, setActiveChatId] = useState<number>(0);
  // Response from RealTime Event
  const [messagePayload, setMessagePayload] = useState<any>();
  // Active messages that user is interacting with
  const [chatMessages, setChatMessages] = useState<ChatMessages[]>([]);
  // Boolean used to determine which form to render in Chat. false=upload true=edit
  const [editingMessage, setEditingMessage] = useState<boolean>(false);
  // UNUSED VARIABLE - NEEDS IMPLEMENTING - will display a count of all messages that have not been rendered
  const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>(0);
  // Current messages that User is interacting with
  const [activeOpenChat, setActiveOpenChat] = useState<ChatsList>({} as ChatsList);
  // Single message that user has chosen to edit
  const [messageToEdit, setMessageToEdit] = useState<ChatMessages>({} as ChatMessages);
  // Complete list of chats a User has initiated
  const [listOfOpenChats, setListOfOpenChats] = useState<ChatsList[]>([{} as ChatsList]);

  useEffect(() => {
    messagePayload?.new && chatUpdater(messagePayload);
  }, [messagePayload]);

  useEffect(() => {
    if (user?.username) {
      fetchChats(user?.username).then((res: PostgrestSingleResponse<any[]>) => {
        if (!res.error && isOpenChatsList(res.data)) {
          setListOfOpenChats(res.data);
        }
        chatSubscription(setMessagePayload, `${user.username}`);
      });
    }
  }, [user]);

  // useEffect(() => {
  //   let totalUnreadCount: number = 0;
  //   if (isOpenChatsList(listOfOpenChats)) {
  //     totalUnreadCount = listOfOpenChats.reduce((acc, curr) => {
  //       const singleChatCount = curr.messages.reduce((acc, curr) => {
  //         return curr.read === false && curr.sender !== user?.username ? acc + 1 : 0;
  //       }, 0);

  //       return acc + singleChatCount;
  //     }, 0);
  //   }

  //   setUnreadMessagesCount(totalUnreadCount);
  // }, [listOfOpenChats]);

  const isOpenChatsList = (value: ChatsList[]): value is ChatsList[] => {
    if (value[0]?.chat_id && value[0]?.user_1 && value[0]?.user_2) {
      return true;
    } else {
      return false;
    }
  };

  const updateAllStatesAfterInsert = (payload: any) => {
    // create mutable array of ListOfOpenChats
    const copyOfListOfChats = [...listOfOpenChats];
    // Get index of chat to add new message to
    const indexOfChatToUpdate = copyOfListOfChats.findIndex(
      (element) => element.chat_id === payload.new.chat_id
    );
    // create mutable array of chatMessages
    const newMessagesArray = [...chatMessages, payload.new];
    // update messages array in ListOfOpenChats at appropriate index
    copyOfListOfChats[indexOfChatToUpdate].messages = newMessagesArray;
    // create mutable object of ActiveOpenChat
    const copyOfActiveChatObject = { ...activeOpenChat };
    // update messages array in ActiveOpenChat
    copyOfActiveChatObject.messages = newMessagesArray;

    // Update states
    setChatMessages(newMessagesArray);
    setListOfOpenChats(copyOfListOfChats);
    setActiveOpenChat(copyOfActiveChatObject);
  };

  const updateAllStatesAfterEdit = (payload: any) => {
    // create mutable array of chatMessages
    const copyOfMessagesArray = [...chatMessages];
    // find index of message to update
    const indexToInsertAt = chatMessages.findIndex((element) => element.id === payload.new.id);
    // splice in edited message
    copyOfMessagesArray.splice(indexToInsertAt, 1, payload.new);

    // create mutable array of ListOfOpenChats
    const copyOfListOfChats = [...listOfOpenChats];
    // Get index of chat to add new message to
    const indexOfChatToUpdate = copyOfListOfChats.findIndex(
      (element) => element.chat_id === payload.new.chat_id
    );

    // update messages array in ListOfOpenChats at appropriate index
    copyOfListOfChats[indexOfChatToUpdate].messages = copyOfMessagesArray;

    setChatMessages(copyOfMessagesArray);
    setListOfOpenChats(copyOfListOfChats);
    setActiveOpenChat(copyOfListOfChats[indexOfChatToUpdate]);
  };

  const removeChatFromListOfChats = async (chat_id: number) => {
    const chatToRemove = await closeChat(chat_id);
    if (chatToRemove.status === 204) {
      setListOfOpenChats(listOfOpenChats.filter((element) => element.chat_id !== chat_id));
      setActiveOpenChat({} as ChatsList);
      setActiveChatId(0);
    } else {
      console.log('SOMETHING WENT WRONG WHILE DELETING CHAT FROM LIST OF CHATS');
    }
  };

  const pessimisticDeleteChatMessage = (message: ChatMessages, status: any) => {
    const newMessagesArray = chatMessages.filter((element) => element.id !== message.id);

    if (status.status === 204 && newMessagesArray.length > 0) {
      setChatMessages(newMessagesArray);
    } else if (status.status === 204 && newMessagesArray.length === 0) {
      setChatMessages(newMessagesArray);
      removeChatFromListOfChats(message.chat_id);
    } else {
      console.log('AN ERROR OCCURED IN PESSIMISTIC DELETE: ', status);
    }
  };

  const chatUpdater = (payload: any) => {
    switch (payload.eventType) {
      case 'INSERT':
        if (chatMessages !== undefined && activeChatId === payload.new.chat_id) {
          updateAllStatesAfterInsert(payload);

          console.log('MESSAGE SUCCESSFULLY ADDED TO CHAT MESSAGES');
          break;
        } else {
          console.log('AN ERROR OCCURED IN CHAT UPDATER. MESSAGE CANNOT BE ADDED TO CHAT MESSAGES');
          break;
        }

      case 'UPDATE':
        if (payload.new !== undefined) {
          updateAllStatesAfterEdit(payload);
          break;
        } else {
          return 'AN ERROR OCCURED IN CHAT UPDATER. MESSAGE HAS NOT BEEN EDITED';
        }

      default:
        console.log('OPERATION DOES NOT MATCH ANY OF THE CASES IN CHAT UPDATER');
        break;
    }
  };

  return (
    <ChatMessageContext.Provider
      value={{
        chatUpdater,
        chatMessages,
        setChatMessages,
        activeChatId,
        setActiveChatId,
        messageToEdit,
        setMessageToEdit,
        activeOpenChat,
        setActiveOpenChat,
        editingMessage,
        setEditingMessage,
        listOfOpenChats,
        setListOfOpenChats,
        unreadMessagesCount,
        setUnreadMessagesCount,
        pessimisticDeleteChatMessage,
      }}
    >
      {children}
    </ChatMessageContext.Provider>
  );
};
